import { Flex, List, Space } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import useNotes from "../../../hooks/useNotes";
import { useNotesStore } from "../../../pages/notes/notesStore";
import { successNotification } from "../../../pages/utils/CommonNotifications";
import { DeleteComponent, formatDate } from "../../../pages/utils/CommonVessels";
import NotesEditor from "./NotesEditor";
import HNotification from "../../custom/notification/HNotification";

export default function Notes({
  appProperties,
  entityId,
  integId,
  onNoteAdded,
  onNoteUpdated,
  onNoteDeleted,
  noteIdToBeFocused,
  setNoteIdToBeFocused,
}) {
  const editorRef = useRef(null);
  const { fetchSavedNotesByEntity, addNote, updateNote, stripHtmlTags, deleteNote, fetchNoteById } = useNotes(appProperties);
  const {
    fetchNotes,
    addNote: addNoteToStore,
    updateNote: updateNoteInStore,
    clearEntityNotes,
    getNotesByEntityId,
    getLoadingByEntityId,
    getActiveNoteId,
    getEditingContent,
    setActiveNoteId,
    setEditingContent,
    deleteNote: deleteNoteInStore,
    getPaginationByEntityId,
    getHasMoreByEntityId,
    setFocusedNoteId,
    setEntityNotesLoading,
    getIsInFocusedMode,
    deactiveFocusNoteMode,
  } = useNotesStore();

  // Get entity-specific state
  const notes = getNotesByEntityId(entityId);
  const pagination = getPaginationByEntityId(entityId);
  const notesLoading = getLoadingByEntityId(entityId);
  const activeNoteId = getActiveNoteId(entityId);
  const editingContent = getEditingContent(entityId);
  const hasMore = getHasMoreByEntityId(entityId);

  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isPaginationLoadingRef = useRef(false);
  const notesContainerRef = useRef(null);

  // Restore scroll position after new data is loaded
  useEffect(() => {
    if (scrollPositionRef.current > 0 && isPaginationLoadingRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
        isPaginationLoadingRef.current = false;
      });
    }
  }, [notes]);

  useEffect(() => {
    if (appProperties && entityId) {
      fetchNotes({
        appProperties,
        entityId,
        fetchSavedNotesByEntity,
        paginationParams: pagination,
      });
    }

    // Cleanup on unmount - clear only this entity's notes
    return () => {
      if (entityId) {
        clearEntityNotes(entityId);
      }
    };
  }, [appProperties, entityId, fetchNotes, fetchSavedNotesByEntity, clearEntityNotes]);

  // Handle click outside to deactivate focus note mode
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if entity is in focused mode
      const isInFocusedMode = getIsInFocusedMode(entityId);

      if (isInFocusedMode && notesContainerRef.current && !notesContainerRef.current.contains(event.target)) {
        deactiveFocusNoteMode(entityId);
        setNoteIdToBeFocused(null);
      }
    };

    // Add event listener when component mounts
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [entityId, getIsInFocusedMode, deactiveFocusNoteMode, setNoteIdToBeFocused, setFocusedNoteId]);

  const handleDelete = (noteId) => {
    deleteNote(noteId, entityId, onNoteDeleted)
      .then((response) => {
        if (response?.success) {
          successNotification("Note deleted successfully");
          deleteNoteInStore(entityId, noteId);
          return response;
        } else {
          throw new Error(response?.data);
        }
      })
      .catch((error) => {
        HNotification.notify({type: "error", message: "Error", description: error?.message});
      });
  };

  const handleSave = async (noteContent) => {
    if (!appProperties || !entityId) {
      HNotification.notify({type: "error", message: "Error", description: "Please select a contact to add a note"});
      return;
    }

    if (!noteContent || !stripHtmlTags(noteContent)) {
      HNotification.notify({type: "error", message: "Error", description: "Note content is required."});
      return;
    }

    if (notesLoading) {
      return;
    }

    try {
      setEntityNotesLoading(entityId, true);
      if (activeNoteId === "NEW_NOTE") {
        const response = await addNote(entityId, noteContent, integId, onNoteAdded);
        if (response?.success && response?.data) {
          addNoteToStore(entityId, response.data);
        }
      } else {
        const response = await updateNote(entityId, activeNoteId, noteContent, onNoteUpdated);
        if (response?.data) {
          updateNoteInStore(entityId, activeNoteId, response.data);
        }
      }
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setEditingContent(entityId, "");
      setActiveNoteId(entityId, "NEW_NOTE");
      setEntityNotesLoading(entityId, false);
    }
  };

  const handleEdit = (noteId, noteContent) => {
    setEditingContent(entityId, noteContent);
    setActiveNoteId(entityId, noteId);
    focusEditor();
  };

  const handleCancel = () => {
    setEditingContent(entityId, "");
    setActiveNoteId(entityId, "NEW_NOTE");
  };

  const focusEditor = () => {
    const editor = document.querySelector(".ql-editor");
    if (editor) {
      editor.focus();
    }
  };

  const handleScroll = (e) => {
    if (!hasMore || notesLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Save current scroll position before loading new data
    if (scrollHeight - scrollTop - clientHeight < 50) {
      scrollPositionRef.current = scrollTop;
      isPaginationLoadingRef.current = true;

      fetchNotes({
        appProperties,
        entityId,
        fetchSavedNotesByEntity,
        paginationParams: pagination,
      });
    }
  };

  return (
    <div ref={notesContainerRef}>
      <Space
        direction="vertical"
        className="w-100 h-100 overflow-auto"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        key={`notes-container-${entityId}`}
      >
        <NoteEditorContainer
          onSave={handleSave}
          isEditing={activeNoteId !== "NEW_NOTE"}
          currentValue={editingContent}
          entityId={entityId}
          setCurrentValue={setEditingContent}
          onCancel={handleCancel}
          editorRef={editorRef}
        />
        <ListNotes
          appProperties={appProperties}
          notes={notes}
          loading={notesLoading}
          onEdit={handleEdit}
          stripHtmlTags={stripHtmlTags}
          onDelete={handleDelete}
          noteIdToBeFocused={noteIdToBeFocused}
          scrollContainerRef={scrollContainerRef}
          fetchNoteById={fetchNoteById} // Pass the function
          entityId={entityId}
          setNoteIdToBeFocused={setNoteIdToBeFocused}
          setFocusedNoteId={setFocusedNoteId}
        />
      </Space>
    </div>
  );
}

function NoteEditorContainer({ onSave, currentValue, entityId, setCurrentValue, isEditing = false, onCancel, editorRef = null }) {
  const handleSave = (noteContent) => {
    if (onSave) {
      onSave(noteContent).finally(() => {
        setCurrentValue(entityId, "");
      });
    }
  };

  return (
    <div className="p-3">
      <NotesEditor
        onSave={handleSave}
        currentValue={currentValue}
        entityId={entityId}
        setCurrentValue={setCurrentValue}
        isEditing={isEditing}
        onCancel={onCancel}
        editorRef={editorRef}
      />
    </div>
  );
}

function ListNotes({ notes, loading, onEdit, appProperties, onDelete, noteIdToBeFocused, fetchNoteById, entityId, setNoteIdToBeFocused }) {
  const { activeFocusNoteMode, deactiveFocusNoteMode, getIsInFocusedMode } = useNotesStore();
  const isInFocusedMode = getIsInFocusedMode(entityId);
  const [deleteModalId, setDeleteModalId] = useState(null);

  useEffect(() => {
    if (!isInFocusedMode && noteIdToBeFocused) {
      setNoteIdToBeFocused(null);
    }
  }, [isInFocusedMode]);

  useEffect(() => {
    if (noteIdToBeFocused) {
      fetchNoteById(noteIdToBeFocused)
        .then((noteData) => {
          if (noteData && noteData?.success && noteData?.data) {
            activeFocusNoteMode(entityId, noteData?.data);
          }
        })
        .catch((error) => {
          HNotification.notify({type: "error", message: "Error", description: error?.message});
          setNoteIdToBeFocused(null);
        });
    }
  }, [noteIdToBeFocused]);

  const decodeNoteContent = (content) => {
    if (!content) return "";
    try {
      let decodedContent = content;

      // Remove trailing = if present (base64 padding)
      if (decodedContent.endsWith("=")) {
        decodedContent = decodedContent.slice(0, -1);
      }

      // Replace + with spaces (URL encoding)
      decodedContent = decodedContent.replace(/\+/g, " ");

      // Decode URL encoding
      decodedContent = decodeURIComponent(decodedContent);

      return decodedContent;
    } catch (error) {
      console.error("Error decoding note content:", error);
      return content; // Return original content if decoding fails
    }
  };

  const clearTemporaryNotesAndSetFocusedNoteIdToBeNull = () => {
    setNoteIdToBeFocused(null);
    deactiveFocusNoteMode(entityId);
  };

  useEffect(() => {
    return () => {
      clearTemporaryNotesAndSetFocusedNoteIdToBeNull();
    };
  }, []);

  // Transform notes data for List component with sorting by modifiedTime
  const sortedNotesData = useMemo(() => {
    if (!notes || notes.length === 0) return [];
    const sortedNotesData = notes
      .map((note) => ({
        key: note.noteId,
        note,
        modifiedTime: new Date(note.modifiedTime || note.createdTime || 0).getTime(),
      }))
      .sort((a, b) => b.modifiedTime - a.modifiedTime) // Sort by modifiedTime descending
      .map(({ note }) => note); // Return only the note objects

    return sortedNotesData;
  }, [notes]);

  if (!notes || notes.length === 0) {
    return <></>;
  }

  return (
    <List
      bordered={false}
      dataSource={sortedNotesData}
      loading={loading}
      renderItem={(note) => {
        const isCurrentNoteIsFocused = noteIdToBeFocused === note.noteId;

        return (
          <List.Item key={note.noteId} className="p-2" style={{ borderBlockEnd: "none" }}>
            <DeleteComponent
              modalOpen={deleteModalId === note?.noteId}
              setModalOpen={setDeleteModalId}
              record={note}
              confirmDeleteIcon={"hs-delete-icon"}
              confirmDeleteBtnName={"Delete"}
              confirmDeleteComponentTitle={"Delete Note?"}
              confirmDeleteComponent={"Are you sure you want to delete this note? This action cannot be undone."}
              appProperties={appProperties}
              handleDelete={() => onDelete(note.noteId)}
            />
            <div className={`hs-border-md-grey hs-border-10 w-100 p-3 ${isCurrentNoteIsFocused ? "hs-on-focus-box" : ""}`}>
              <Flex vertical className="h-100">
                <div>
                  <div className="w-100 d-flex justify-content-between">
                    <small className="text-muted">
                      <span className="hs-fw-600 hs-fs-12-px">
                        {appProperties?.currentUser?.osyncUserId === note?.osyncUserId
                          ? "You"
                          : appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user.osyncUserId === note.osyncUserId)?.firstName}
                      </span>
                    </small>
                    <div className="d-flex align-items-center">
                      <div
                        onClick={() => onEdit(note.noteId, decodeNoteContent(note.note))}
                        className="actionIconsSprite2 note-icons edit-note-icon"
                      />
                      <div className="actionIconsSprite2 note-icons delete-note-icon" onClick={() => setDeleteModalId(note?.noteId)} />
                    </div>
                  </div>
                  <div className="hs-text-white-space-pre-wrap text-break" dangerouslySetInnerHTML={{ __html: decodeNoteContent(note.note) }} />
                </div>
                <span className="hs-fs-10-px text-muted ms-auto">{formatDate(note.modifiedTime)}</span>
              </Flex>
            </div>
          </List.Item>
        );
      }}
    />
  );
}
